// src/components/ChatDM.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useChat } from '../contexts/ChatContext.jsx';
import './ChatDM.css';
import { FaUserCircle, FaVolumeUp, FaVolumeMute, FaFilePdf, FaFileAlt } from 'react-icons/fa';
import { showToast } from '../utils/toast';
import { isClientUser, readStoredUser } from '../utils/userFlags.js';

const STATE_KEY = 'chatDMState';
const CHAT_DM_ANIMATION_MS = 900;
const normalizeMessage = (m = {}) => {
  if (!m || typeof m !== 'object') return {};
  const senderId = Number(m.sender_id || m.from_userid || 0) || 0;
  const recipientId = Number(m.recipient_id || m.to_userid || 0) || 0;
  const created = m.created_at || m.timestamp || m.date || m.sent_at || null;
  const attachments = Array.isArray(m.attachments) ? m.attachments : [];
  return {
    ...m,
    sender_id: senderId,
    recipient_id: recipientId,
    created_at: created,
    attachments,
  };
};
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
function clampState(saved) {
  const vpW = window.innerWidth || 1024;
  const vpH = window.innerHeight || 768;
  const minW = 320, minH = 380, maxH = 900;
  const width = clamp(Number(saved?.width ?? 420), minW, vpW);
  const height = clamp(Number(saved?.height ?? 560), minH, Math.min(maxH, vpH));
  const maxX = Math.max(0, vpW - width);
  const maxY = Math.max(0, vpH - height);
  const x = clamp(Number(saved?.x ?? 80), 0, maxX);
  const y = clamp(Number(saved?.y ?? 80), 0, maxY);
  return { width, height, x, y };
}

export default function ChatDM() {
  const API = (import.meta.env.VITE_API_URL ? String(import.meta.env.VITE_API_URL).replace(/\/$/, '') : '');
  const {
    isDmOpen, setIsDmOpen,
    dmAnchorRect,
    activeDmUserId, setActiveDmUserId,
    unreadByUser, unreadCount, markThreadRead,
    pushStatus
  } = useChat();

  const [size, setSize] = useState({ width: 420, height: 560 });
  const [position, setPosition] = useState({ x: 80, y: 80 });
  const [dmList, setDmList] = useState([]);
  const [online, setOnline] = useState([]);
  const [userList, setUserList] = useState([]);
  const [dmMsgs, setDmMsgs] = useState([]);
  const msgIdSetRef = useRef(new Set()); // track message ids in current thread to avoid duplicates
  const staleThreadsRef = useRef(new Set());
  const [refreshKey, setRefreshKey] = useState(0);
  const [input, setInput] = useState('');
  const [fileToSend, setFileToSend] = useState(null);
  const userIdRef = useRef(0);
  const messagesRef = useRef(null);
  const notifyReadyRef = useRef(false);
  const audioCtxRef = useRef(null);
  const lastLoadedUserRef = useRef(null);
  const [soundOn, setSoundOn] = useState(() => {
    try { return localStorage.getItem('chat.dm.sound') !== '0'; } catch { return true; }
  });
  const soundOnRef = useRef(soundOn);
  useEffect(() => { soundOnRef.current = soundOn; }, [soundOn]);
  useEffect(() => {
    try { localStorage.setItem('chat.dm.sound', soundOn ? '1' : '0'); } catch {}
  }, [soundOn]);
  const activeDmIdRef = useRef(null);
  const inputRef = useRef(null);
  const onlineRef = useRef([]);
  useEffect(() => { activeDmIdRef.current = activeDmUserId; }, [activeDmUserId]);
  useEffect(() => { onlineRef.current = online; }, [online]);

  // Block clients from rendering DM UI
  const isClient = (() => {
    try {
      return isClientUser(readStoredUser());
    } catch { return false; }
  })();
  const [shouldRender, setShouldRender] = useState(isDmOpen && !isClient);
  const [isWindowVisible, setIsWindowVisible] = useState(isDmOpen && !isClient);

  // --- Desktop notifications + sound ---
  const tryRequestNotify = useCallback(() => {
    try {
      if (!('Notification' in window)) return;
      if (Notification.permission === 'granted') { notifyReadyRef.current = true; return; }
      if (Notification.permission === 'denied') { notifyReadyRef.current = false; return; }
      Notification.requestPermission().then((perm) => {
        notifyReadyRef.current = (perm === 'granted');
      }).catch(()=>{ notifyReadyRef.current = false; });
    } catch { notifyReadyRef.current = false; }
  }, []);

  const playPing = useCallback(() => {
    if (!soundOnRef.current) return;
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      if (!audioCtxRef.current) audioCtxRef.current = new AudioCtx();
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') {
        // Try resuming (autoplay policies)
        try { ctx.resume(); } catch {}
      }
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.setValueAtTime(880, ctx.currentTime);
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.18);
      o.connect(g).connect(ctx.destination);
      o.start();
      o.stop(ctx.currentTime + 0.2);
    } catch {}
  }, []);

  const showDesktopNotification = useCallback((title, body) => {
    try {
      if (!notifyReadyRef.current) return;
      if (document.hasFocus() && document.visibilityState === 'visible') return; // avoid spam when focused
      const n = new Notification(title || 'New message', { body: String(body || '').slice(0, 140) });
      setTimeout(() => { try { n.close(); } catch {} }, 4000);
    } catch {}
  }, []);

  const onlineIds = useMemo(() => new Set((online || []).map(u => Number(u.userid))), [online]);

  const upsertConversation = useCallback((userId, patch = {}) => {
    const numericId = Number(userId || 0);
    if (!Number.isFinite(numericId) || numericId <= 0) return;
    setDmList((prev) => {
      const existing = Array.isArray(prev) ? prev.find((entry) => Number(entry?.userid || entry?.userId || 0) === numericId) : null;
      const fallbackUser =
        userList.find((entry) => Number(entry?.userid || 0) === numericId) ||
        online.find((entry) => Number(entry?.userid || 0) === numericId) ||
        null;
      const next = {
        ...(existing || {}),
        ...(fallbackUser || {}),
        userid: numericId,
        userId: numericId,
        ...patch,
        lastAt: patch.lastAt || existing?.lastAt || new Date().toISOString(),
      };
      const rest = (prev || []).filter((entry) => Number(entry?.userid || entry?.userId || 0) !== numericId);
      return [next, ...rest];
    });
  }, [online, userList]);

  const sortUsers = useCallback((list = []) => {
    const conversationMap = new Map((dmList || []).map((entry) => [
      Number(entry?.userid || entry?.userId || 0),
      entry,
    ]));
    return [...list].sort((a, b) => {
      const aId = Number(a?.userid || 0);
      const bId = Number(b?.userid || 0);
      const aUnread = Number(unreadByUser?.[aId] || 0);
      const bUnread = Number(unreadByUser?.[bId] || 0);
      if (aUnread !== bUnread) return bUnread - aUnread;

      const aLast = Date.parse(conversationMap.get(aId)?.lastAt || '') || 0;
      const bLast = Date.parse(conversationMap.get(bId)?.lastAt || '') || 0;
      if (aLast !== bLast) return bLast - aLast;

      const aName = String(a?.fullname || a?.login || `User #${aId}`).toLowerCase();
      const bName = String(b?.fullname || b?.login || `User #${bId}`).toLowerCase();
      return aName.localeCompare(bName);
    });
  }, [dmList, unreadByUser]);

  const sortedOnlineUsers = useMemo(() => {
    const visible = (online || []).filter((u) => Number(u.userid) !== Number(userIdRef.current));
    return sortUsers(visible);
  }, [online, sortUsers]);

  const sortedOfflineUsers = useMemo(() => {
    const visible = (userList || []).filter((u) => !onlineIds.has(Number(u.userid)));
    return sortUsers(visible);
  }, [onlineIds, sortUsers, userList]);
  const focusInput = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    try { el.focus({ preventScroll: true }); } catch { try { el.focus(); } catch {} }
    const len = el.value.length;
    try { el.selectionStart = len; el.selectionEnd = len; } catch {}
  }, []);

  // Load current user id
  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem('currentUser') || localStorage.getItem('user') || '{}');
      userIdRef.current = Number(u?.userid || 0) || 0;
    } catch { userIdRef.current = 0; }
  }, []);

  // Hydrate size/pos
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!isDmOpen || hydratedRef.current) return;
    try {
      const raw = localStorage.getItem(STATE_KEY);
      const c = clampState(raw ? JSON.parse(raw) : {});
      setSize({ width: c.width, height: c.height });
      setPosition({ x: c.x, y: c.y });
    } catch {}
    hydratedRef.current = true;
  }, [isDmOpen]);
  useEffect(() => {
    if (!hydratedRef.current) return;
    try { localStorage.setItem(STATE_KEY, JSON.stringify({ ...size, ...position })); } catch {}
  }, [size, position]);

  // Dragging
  const startDragging = useCallback((e) => {
    e.preventDefault();
    const startX = e.clientX, startY = e.clientY;
    const init = { ...position };
    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      const vpW = window.innerWidth, vpH = window.innerHeight;
      const x = clamp(init.x + dx, 0, Math.max(0, vpW - size.width));
      const y = clamp(init.y + dy, 0, Math.max(0, vpH - size.height));
      setPosition({ x, y });
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp, { once: true });
  }, [position, size.width, size.height]);

  // Resizing
  const startResizing = useCallback((direction, e) => {
    e.preventDefault();
    e.stopPropagation();
    try { document.body.classList.add('chatdm-resizing'); } catch {}
    const startX = e.clientX, startY = e.clientY;
    const startWidth = size.width, startHeight = size.height;
    const startLeft = position.x, startTop = position.y;
    const minW = 320, minH = 380, maxHCap = 900;
    const onMove = (ev) => {
      const vpW = window.innerWidth, vpH = window.innerHeight;
      const maxW = vpW;
      const maxH = Math.min(maxHCap, vpH);
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      const fromLeft = direction.includes('left');
      const fromTop = direction.includes('top');

      let nextWidth = startWidth + (fromLeft ? -dx : dx);
      let nextHeight = startHeight + (fromTop ? -dy : dy);
      nextWidth = clamp(nextWidth, minW, maxW);
      nextHeight = clamp(nextHeight, minH, maxH);

      let nextX = fromLeft ? startLeft + (startWidth - nextWidth) : startLeft;
      let nextY = fromTop ? startTop + (startHeight - nextHeight) : startTop;

      nextX = clamp(nextX, 0, Math.max(0, vpW - nextWidth));
      nextY = clamp(nextY, 0, Math.max(0, vpH - nextHeight));

      if (fromLeft) {
        nextWidth = clamp(startLeft + startWidth - nextX, minW, maxW);
        nextX = clamp(startLeft + startWidth - nextWidth, 0, Math.max(0, vpW - nextWidth));
      }
      if (fromTop) {
        nextHeight = clamp(startTop + startHeight - nextY, minH, maxH);
        nextY = clamp(startTop + startHeight - nextHeight, 0, Math.max(0, vpH - nextHeight));
      }

      setSize({ width: nextWidth, height: nextHeight });
      setPosition({ x: nextX, y: nextY });
    };
    const onUp = () => {
      try { document.body.classList.remove('chatdm-resizing'); } catch {}
      document.removeEventListener('mousemove', onMove);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp, { once: true });
  }, [position, size]);

  // Intelligent auto-scroll: only when near bottom or own message
  const [nearBottom, setNearBottom] = useState(true);
  useEffect(() => {
    const el = messagesRef.current;
    if (!el) return;
    const onScroll = () => {
      try {
        const threshold = 48;
        const pos = el.scrollTop + el.clientHeight;
        setNearBottom((el.scrollHeight - pos) <= threshold);
      } catch {}
    };
    el.addEventListener('scroll', onScroll);
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    const el = messagesRef.current;
    if (!el) return;
    const last = dmMsgs[dmMsgs.length - 1];
    const isOwn = last && Number(last.sender_id) === Number(userIdRef.current);
    if (nearBottom || isOwn) {
      try { el.scrollTop = el.scrollHeight; } catch {}
    }
  }, [dmMsgs, nearBottom]);

  useEffect(() => {
    if (isClient) {
      setShouldRender(false);
      setIsWindowVisible(false);
      return undefined;
    }

    let frameId = 0;
    let timeoutId = 0;

    if (isDmOpen) {
      setShouldRender(true);
      frameId = window.requestAnimationFrame(() => setIsWindowVisible(true));
    } else {
      setIsWindowVisible(false);
      timeoutId = window.setTimeout(() => setShouldRender(false), CHAT_DM_ANIMATION_MS);
    }

    return () => {
      if (frameId) window.cancelAnimationFrame(frameId);
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [isDmOpen, isClient]);
  useEffect(() => {
    if (!shouldRender || !isWindowVisible) return;
    const frameId = window.requestAnimationFrame(() => focusInput());
    return () => window.cancelAnimationFrame(frameId);
  }, [shouldRender, isWindowVisible, focusInput]);
  useEffect(() => {
    if (!isWindowVisible) return;
    const frameId = window.requestAnimationFrame(() => focusInput());
    return () => window.cancelAnimationFrame(frameId);
  }, [activeDmUserId, isWindowVisible, focusInput]);
  useEffect(() => { if (isDmOpen) tryRequestNotify(); }, [isDmOpen, tryRequestNotify]);

  // Unlock AudioContext on first user interaction (for browsers with autoplay restrictions)
  useEffect(() => {
    const unlock = async () => {
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (!AudioCtx) return;
        if (!audioCtxRef.current) audioCtxRef.current = new AudioCtx();
        if (audioCtxRef.current.state === 'suspended') {
          try { await audioCtxRef.current.resume(); } catch {}
        }
      } catch {}
    };
    document.addEventListener('click', unlock, { once: true });
    document.addEventListener('touchstart', unlock, { once: true });
    return () => {
      document.removeEventListener('click', unlock);
      document.removeEventListener('touchstart', unlock);
    };
  }, []);

  // Fetch conversations and user directory when opening (presence via chat:presence)
  useEffect(() => {
    if (!isDmOpen) return;
    (async () => {
      try {
        const r = await fetch(`${API}/api/messages/conversations`, { credentials: 'include' });
        if (r.ok) setDmList(await r.json());
      } catch {}
      try {
        const r = await fetch(`${API}/api/messages/users`, { credentials: 'include' });
        if (r.ok) setUserList(await r.json());
      } catch {}
      // Seed presence immediately
      try {
        const r = await fetch(`${API}/api/messages/online`, { credentials: 'include' });
        const j = await r.json().catch(()=>({ online: [] }));
        if (Array.isArray(j?.online)) setOnline(j.online);
      } catch {}
    })();
  }, [isDmOpen]);

  // Auto-load selected thread when chat opens (no click needed) — single fetch if empty

  // Remove focus refresh to avoid interrupting user scroll; WS push keeps it fresh

  // No periodic refresh; rely on push updates

  // Load thread when selecting user
  const loadingThreadRef = useRef(false);
  const clearThreadStale = useCallback((uid) => {
    if (!uid) return;
    try {
      const id = Number(uid);
      if (!Number.isFinite(id) || id <= 0) return;
      staleThreadsRef.current.delete(id);
    } catch {}
  }, []);
  const markThreadStale = useCallback((uid) => {
    if (!uid) return;
    try {
      const id = Number(uid);
      if (!Number.isFinite(id) || id <= 0) return;
      staleThreadsRef.current.add(id);
      setRefreshKey((k) => k + 1);
    } catch {}
  }, []);
  const openThread = useCallback(async (uid, { force } = {}) => {
    const numericId = Number(uid);
    if (!Number.isFinite(numericId) || numericId <= 0) return;
    setActiveDmUserId(numericId);
    if (!force && loadingThreadRef.current && lastLoadedUserRef.current === numericId) return;
    loadingThreadRef.current = true;
    try {
      const r = await fetch(`${API}/api/messages/with/${numericId}`, { credentials: 'include' });
      if (r.ok) {
        const arr = await r.json();
        const list = Array.isArray(arr) ? arr.map(normalizeMessage) : [];
        setDmMsgs(list);
        try {
          const s = new Set();
          for (const m of list) {
            const id = Number(m?.id || 0);
            if (id) s.add(id);
          }
          msgIdSetRef.current = s;
        } catch {}
        lastLoadedUserRef.current = numericId;
        clearThreadStale(numericId);
      } else {
        showToast({ type:'error', message:`Failed to load messages (HTTP ${r.status})`, align:'center', pill:true });
      }
    } catch (e) {
      showToast({ type:'error', message:`Failed to load messages: ${e?.message || 'network error'}`, align:'center', pill:true });
    } finally {
      loadingThreadRef.current = false;
    }
    markThreadRead(numericId);
  }, [API, markThreadRead, clearThreadStale]);

  useEffect(() => {
    if (!isDmOpen) return;
    const uid = Number(activeDmUserId || 0);
    if (!uid) return;
    const stale = staleThreadsRef.current.has(uid);
    if (stale || dmMsgs.length === 0 || lastLoadedUserRef.current !== uid) {
      const t = setTimeout(() => {
        lastLoadedUserRef.current = uid;
        openThread(uid, { force: true });
      }, 0);
      return () => clearTimeout(t);
    }
  }, [isDmOpen, activeDmUserId, dmMsgs.length, openThread, refreshKey]);

  // Send API with small retry
  const sendApi = async (toUserId, content) => {
    const maxAttempts = 2;
    let lastErr = null;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const resp = await fetch(`${API}/api/messages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ to: toUserId, content })
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        return await resp.json().catch(() => ({}));
      } catch (e) {
        lastErr = e;
        await new Promise(r => setTimeout(r, attempt * 200));
      }
    }
    throw lastErr || new Error('Send failed');
  };

  const uploadAttachment = async (messageId, file) => {
    if (!file) return null;
    const form = new FormData();
    form.append('file', file);
    const resp = await fetch(`${API}/api/messages/${messageId}/attachments`, {
      method: 'POST',
      body: form,
      credentials: 'include'
    });
    const j = await resp.json().catch(()=>({}));
    if (!resp.ok || j?.success === false) throw new Error(j?.error || `Upload failed (HTTP ${resp.status})`);
    return j?.attachment || null;
  };

  // Presence via global push (ChatContext) only — single channel per page
  useEffect(() => {
    const onPresence = (e) => {
      try {
        const arr = e?.detail?.online || [];
        if (!Array.isArray(arr)) return;
        // Normalize: accept either [{userid, login, fullname}...] or [id...]
        let list;
        if (arr.length && typeof arr[0] === 'number') {
          list = arr.map(id => {
            const match = userList.find(x => Number(x.userid) === Number(id)) || dmList.find(x => Number(x.userid) === Number(id));
            return { userid: Number(id), login: match?.login || undefined, fullname: match?.fullname || undefined };
          });
        } else {
          list = arr.map(u => ({ userid: Number(u.userid || 0), login: u.login, fullname: u.fullname }));
        }
        setOnline(list);
      } catch {}
    };
    window.addEventListener('chat:presence', onPresence);
    return () => window.removeEventListener('chat:presence', onPresence);
  }, [userList, dmList]);

  // Listen globally for new messages even when the DM window is closed
  useEffect(() => {
    const handler = (e) => {
      try {
        const raw = e?.detail?.message || {};
        const m = normalizeMessage(raw);
        const me = Number(userIdRef.current || 0);
        const senderId = Number(m.sender_id || 0) || 0;
        const recipientId = Number(m.recipient_id || 0) || 0;
        if (!senderId || !recipientId) return;
        const other = senderId === me ? recipientId : senderId;
        if (!other) return;

        upsertConversation(other, { lastAt: m.created_at || new Date().toISOString() });

        if (senderId === me) {
          setDmMsgs(prev => prev.map(msg => {
            if (!msg) return msg;
            const isOptimistic = !msg.id && Number(msg.sender_id) === me && Number(msg.recipient_id) === recipientId && msg.content === m.content;
            if (!isOptimistic) return msg;
            try { const mid = Number(m.id || 0); if (mid) msgIdSetRef.current.add(mid); } catch {}
            return normalizeMessage({ ...msg, id: m.id, created_at: m.created_at });
          }));
          return;
        }

        if (!activeDmIdRef.current) {
          if (isDmOpen) {
            openThread(other, { force: true });
          } else {
            try { setActiveDmUserId(Number(other)); } catch {}
          }
          playPing();
          showDesktopNotification('New message', m.content);
          return;
        }

        const isActive = activeDmUserId && Number(other) === Number(activeDmUserId);
        if (isActive) {
          // Avoid duplicates when a message arrives while/after thread loads
          const mid = Number(m.id || 0);
          if (mid && msgIdSetRef.current.has(mid)) {
            // already present — skip
          } else {
            setDmMsgs(prev => [...prev, m]);
            if (mid) try { msgIdSetRef.current.add(mid); } catch {}
          }
          if (isDmOpen) {
            try { markThreadRead(Number(other)); } catch {}
          } else {
            markThreadStale(other);
          }
        } else {
          markThreadStale(other);
        }
        playPing();
        showDesktopNotification('New message', m.content);
      } catch {}
    };
    window.addEventListener('chat:newMessage', handler);
    return () => window.removeEventListener('chat:newMessage', handler);
  }, [activeDmUserId, isDmOpen, markThreadRead, markThreadStale, openThread, playPing, setActiveDmUserId, showDesktopNotification, upsertConversation]);

  // Listen for attachment events to patch into current thread
  useEffect(() => {
    const onAtt = (e) => {
      try {
        const att = e?.detail?.attachment || null;
        if (!att) return;
        setDmMsgs(prev => prev.map(m => (Number(m?.id || 0) === Number(att.message_id) ? normalizeMessage({ ...m, attachments: [ ...(m.attachments || []), att ] }) : m)));
      } catch {}
    };
    window.addEventListener('chat:attachment', onAtt);
    return () => window.removeEventListener('chat:attachment', onAtt);
  }, []);

  if (!shouldRender || isClient) return null;

  // --- Formatting helpers ---
  const fmtTime = (d) => {
    try {
      const dt = (d instanceof Date) ? d : new Date(d);
      return dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
    } catch { return ''; }
  };
  const dayKey = (d) => {
    try {
      const dt = (d instanceof Date) ? d : new Date(d);
      return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
    } catch { return 'unknown'; }
  };
  const dayLabel = (d) => {
    try {
      const dt = (d instanceof Date) ? d : new Date(d);
      const now = new Date();
      const todayKey = dayKey(now);
      const yest = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
      const dk = dayKey(dt);
      if (dk === todayKey) return 'Today';
      if (dk === dayKey(yest)) return 'Yesterday';
      return dt.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    } catch { return ''; }
  };

  const fallbackAnchor = {
    left: (window.innerWidth || 1280) - 48,
    top: 18,
    width: 36,
    height: 36,
  };
  const anchor = dmAnchorRect || fallbackAnchor;
  const anchorCenterX = Number(anchor.left || 0) + (Number(anchor.width || 0) / 2);
  const anchorCenterY = Number(anchor.top || 0) + (Number(anchor.height || 0) / 2);
  const modalCenterX = position.x + (size.width / 2);
  const modalCenterY = position.y + (size.height / 2);
  const motionStyle = {
    '--chatdm-enter-x': `${anchorCenterX - modalCenterX}px`,
    '--chatdm-enter-y': `${anchorCenterY - modalCenterY}px`,
    '--chatdm-origin-x': `${anchorCenterX - position.x}px`,
    '--chatdm-origin-y': `${anchorCenterY - position.y}px`,
  };

  return (
    <div className={`chatdm-overlay ${isWindowVisible ? 'is-open' : 'is-closing'}`}>
      <div
        className={`chatdm-modal ${isWindowVisible ? 'is-open' : 'is-closing'}`}
        style={{ ...motionStyle, width: size.width, height: size.height, left: position.x, top: position.y }}
      >
        <div className="chatdm-header" onMouseDown={startDragging}>
          <h3>Messages</h3>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <button
              className="chatdm-sound"
              onClick={async (e) => {
                e.stopPropagation();
                setSoundOn(v => !v);
                try {
                  if (!soundOnRef.current) {
                    const AudioCtx = window.AudioContext || window.webkitAudioContext;
                    if (AudioCtx) {
                      if (!audioCtxRef.current) audioCtxRef.current = new AudioCtx();
                      if (audioCtxRef.current.state === 'suspended') {
                        try { await audioCtxRef.current.resume(); } catch {}
                      }
                    }
                  }
                } catch {}
              }}
              title={soundOn ? 'Mute chat sounds' : 'Enable chat sounds'}
              aria-label={soundOn ? 'Mute chat sounds' : 'Enable chat sounds'}
            >
              {soundOn ? <FaVolumeUp /> : <FaVolumeMute />}
            </button>
            <button
              className="chatdm-clear"
              onClick={async (e) => {
                e.stopPropagation();
                const uid = Number(activeDmUserId || 0);
                if (!uid) return;
                const ok = window.confirm('Clear this conversation from your view? The other user will still see the history.');
                if (!ok) return;
                try {
                  const resp = await fetch(`${API}/api/messages/with/${uid}/clear`, { method: 'POST', credentials: 'include' });
                  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                  // Reset local thread state
                  setDmMsgs([]);
                  try { msgIdSetRef.current = new Set(); } catch {}
                  try { markThreadRead(uid); } catch {}
                  showToast({ type: 'success', message: 'Conversation cleared', align: 'center', pill: true });
                } catch (err) {
                  showToast({ type: 'error', message: `Failed to clear: ${err?.message || 'server error'}`, align: 'center', pill: true });
                }
              }}
              title="Clear this conversation"
              aria-label="Clear conversation"
              style={{ fontSize: 13, padding: '4px 8px', borderRadius: 6, border: '1px solid #0ea5e9', background: 'transparent', color: 'var(--text-color)', cursor: 'pointer' }}
            >
              Clear
            </button>
            <button className="chatdm-close" onClick={() => setIsDmOpen(false)}>×</button>
          </div>
        </div>
        <div className="chatdm-body">
          <div className="chatdm-sidebar">
            <div className="chatdm-section">
              <div className="chatdm-section-title">Online Users</div>
              <div className="chatdm-online-icons">
                {sortedOnlineUsers
                  .map(u => (
                    <div key={`on-${u.userid}`}
                      className={`chatdm-online-user ${Number(activeDmUserId) === Number(u.userid) ? 'active' : ''}`}
                      onClick={() => openThread(u.userid)}
                      title={u.fullname || u.login}
                    >
                      <FaUserCircle className="chatdm-online-avatar" />
                      <span className="chatdm-online-name">{u.fullname || u.login || `User #${Number(u.userid)}`}</span>
                      {!!(unreadByUser?.[u.userid]) && (
                        <span className="chatdm-badge" style={{ marginLeft: 'auto' }}>
                          {unreadByUser[u.userid] > 99 ? '99+' : unreadByUser[u.userid]}
                        </span>
                      )}
                    </div>
                  ))}
              </div>
            </div>
            <div className="chatdm-section" style={{ marginTop: 10 }}>
              <div className="chatdm-section-title">Offline Users</div>
              {sortedOfflineUsers
                .map(u => (
                  <div key={`off-${u.userid}`}
                    className={`chatdm-user ${Number(activeDmUserId) === Number(u.userid) ? 'active' : ''} offline`}
                    onClick={() => openThread(u.userid)}
                    title={u.login}
                  >
                    <div className="chatdm-user-left">
                      <span className="chatdm-avatar"><FaUserCircle /></span>
                      <span className="chatdm-name">{u.fullname || u.login || `User #${Number(u.userid)}`}</span>
                    </div>
                    {!!(unreadByUser?.[u.userid]) && (
                      <span className="chatdm-badge">{unreadByUser[u.userid] > 99 ? '99+' : unreadByUser[u.userid]}</span>
                    )}
                  </div>
                ))}
            </div>
          </div>
          <div className="chatdm-thread">
            <div className="chatdm-messages" ref={messagesRef}>
              {(() => {
                const out = [];
                let lastKey = null;
                dmMsgs.forEach((m, i) => {
                  const dkey = dayKey(m.created_at || m.date || m.sent_at || new Date());
                  if (dkey !== lastKey) {
                    lastKey = dkey;
                    out.push(
                      <div key={`day-${dkey}-${i}`} className="chatdm-day"><span>{dayLabel(m.created_at || m.date || m.sent_at || new Date())}</span></div>
                    );
                  }
                  out.push(
                    <div key={`msg-${i}`} className={`chatdm-msg ${Number(m.sender_id || 0) === Number(userIdRef.current || 0) ? 'self' : 'other'}`}>
                      <div className="chatdm-msg-inner">
                        <span className="chatdm-time chatdm-time-top">{fmtTime(m.created_at || m.date || m.sent_at || new Date())}</span>
                        <div>{m.content}</div>
                        {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                          <div className="chatdm-atts">
                            {m.attachments.map(att => {
                              const url = `${API}/api/messages/attachments/${att.id}`;
                              const name = att.filename || 'Attachment';
                              const isImg = typeof att.mimetype === 'string' && att.mimetype.toLowerCase().startsWith('image/');
                              const isPdf = String(att.mimetype || '').toLowerCase() === 'application/pdf';
                              const bytes = Number(att.filesize || 0) || 0;
                              let size = '';
                              if (bytes > 0) {
                                if (bytes < 1024) size = ` (${bytes} B)`;
                                else if (bytes < 1024*1024) size = ` (${Math.round(bytes/1024)} KB)`;
                                else size = ` (${(bytes/1024/1024).toFixed(1)} MB)`;
                              }
                              return (
                                <div key={att.id} className="chatdm-att">
                                  {isImg ? (
                                    <a href={url} target="_blank" rel="noopener noreferrer" title={name} className="chatdm-thumb-link">
                                      <img src={url} alt={name} className="chatdm-thumb" />
                                    </a>
                                  ) : (
                                    <a href={url} target="_blank" rel="noopener noreferrer" className="chatdm-file">
                                      {isPdf ? <FaFilePdf className="chatdm-file-icon pdf" /> : <FaFileAlt className="chatdm-file-icon" />}
                                      <span className="chatdm-file-name" title={name}>{name}</span>
                                      <span className="chatdm-file-size">{size}</span>
                                    </a>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {/* time moved to top */}
                      </div>
                    </div>
                  );
                });
                return out;
              })()}
            </div>
            <div className="chatdm-input">
              <input type="file" style={{ display:'none' }} id="chatdm-file"
                onChange={(e)=>{ const f=e.target.files?.[0]; setFileToSend(f||null); }} />
              <button className="chatdm-attach" onClick={()=>document.getElementById('chatdm-file').click()} title={fileToSend?`Selected: ${fileToSend.name}`:'Attach a file'} aria-label="Attach file">📎</button>
              {fileToSend && (
                <span className="chatdm-file-hint" title={fileToSend.name}>
                  {fileToSend.name.length > 24 ? fileToSend.name.slice(0,21) + '…' : fileToSend.name}
                </span>
              )}
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={async (e) => { if (e.key === 'Enter') {
                  const txt = input.trim(); if (!txt && !fileToSend) return;
                  if (!activeDmUserId) { showToast({ type:'error', message:'Pick a conversation first', align:'center', pill:true }); return; }
                  const optimistic = normalizeMessage({ sender_id: userIdRef.current, recipient_id: activeDmUserId, content: txt, created_at: new Date().toISOString(), attachments: [] });
                  setDmMsgs(prev => [...prev, optimistic]);
                  setInput('');
                  const toId = Number(activeDmUserId);
                  try {
                    const resp = await sendApi(toId, txt);
                    const newId = resp?.id;
                    const created = resp?.created_at;
                    setDmMsgs(prev => prev.map(m => (m === optimistic ? normalizeMessage({ ...m, id: newId || m.id, created_at: created || m.created_at }) : m)));
                    try { const mid = Number(newId || 0); if (mid) msgIdSetRef.current.add(mid); } catch {}
                    if (fileToSend && newId) {
                      try {
                        const att = await uploadAttachment(newId, fileToSend);
                        if (att) {
                          setDmMsgs(prev => prev.map(m => (m === optimistic ? normalizeMessage({ ...m, attachments: [att] }) : m)));
                        }
                      } catch (err) {
                        showToast({ type:'error', message:`Attachment failed: ${err?.message || 'upload error'}`, align:'center', pill:true });
                      }
                    }
                  }
                  catch (err) { showToast({ type:'error', message:`Send failed: ${err?.message || 'network error'}`, align:'center', pill:true }); }
                  setFileToSend(null);
                }}}
                placeholder={activeDmUserId ? 'Type a message…' : 'Select a conversation…'}
              />
              <button
                onClick={async () => {
                  const txt = input.trim(); if (!txt && !fileToSend) return;
                  if (!activeDmUserId) { showToast({ type:'error', message:'Pick a conversation first', align:'center', pill:true }); return; }
                  const optimistic = normalizeMessage({ sender_id: userIdRef.current, recipient_id: activeDmUserId, content: txt, created_at: new Date().toISOString(), attachments: [] });
                  setDmMsgs(prev => [...prev, optimistic]);
                  setInput('');
                  const toId = Number(activeDmUserId);
                  try {
                    const resp = await sendApi(toId, txt);
                    const newId = resp?.id;
                    const created = resp?.created_at;
                    setDmMsgs(prev => prev.map(m => (m === optimistic ? normalizeMessage({ ...m, id: newId || m.id, created_at: created || m.created_at }) : m)));
                    try { const mid = Number(newId || 0); if (mid) msgIdSetRef.current.add(mid); } catch {}
                    if (fileToSend && newId) {
                      try {
                        const att = await uploadAttachment(newId, fileToSend);
                        if (att) {
                          setDmMsgs(prev => prev.map(m => (m === optimistic ? normalizeMessage({ ...m, attachments: [att] }) : m)));
                        }
                      } catch (err) {
                        showToast({ type:'error', message:`Attachment failed: ${err?.message || 'upload error'}`, align:'center', pill:true });
                      }
                    }
                  }
                  catch (err) { showToast({ type:'error', message:`Send failed: ${err?.message || 'network error'}`, align:'center', pill:true }); }
                  setFileToSend(null);
                }}
                disabled={!input.trim() && !fileToSend}
              >Send</button>
            </div>
          </div>
        </div>
        <div className="chatdm-resize chatdm-resize--top-left" onMouseDown={(e) => startResizing('top-left', e)} />
        <div className="chatdm-resize chatdm-resize--top-right" onMouseDown={(e) => startResizing('top-right', e)} />
        <div className="chatdm-resize chatdm-resize--bottom-left" onMouseDown={(e) => startResizing('bottom-left', e)} />
        <div className="chatdm-resize chatdm-resize--bottom-right" onMouseDown={(e) => startResizing('bottom-right', e)} />
      </div>
    </div>
  );
}
